import MediaImg from '../components/MediaImg';
import React, { createContext, useCallback, useContext, useEffect, useMemo, useRef, useState } from "react";
import axios from "axios";
import { useLocation } from "react-router-dom";
import { MessageCircle, Send, X, Mic, Paperclip, Image as ImageIcon, FileText, Upload, Radio, Play, Pause } from "lucide-react";
import ChatMessageDeleteButton from "./ChatMessageDeleteButton";
import { Button } from "./ui/button";
import { Input } from "./ui/input";
import { toast } from "sonner";
import { useAuth } from "../context/AuthContext";
import { useNavigate } from "react-router-dom";

const ChatContext = createContext(null);
import { API_URL, WS_URL } from '../config/api';
import { createChatRealtime, createGlobalRealtime } from '../services/chatRealtime';
import notificationService from '../services/notificationService';
const API = API_URL;
const WS = WS_URL;

// Custom Audio Player Component (WhatsApp-style)
const CustomAudioPlayer = ({ audioUrl, duration }) => {
  const [isPlaying, setIsPlaying] = useState(false);
  const [currentTime, setCurrentTime] = useState(0);
  const [totalDuration, setTotalDuration] = useState(duration || 0);
  const audioRef = useRef(null);

  const handlePlayPause = () => {
    if (audioRef.current) {
      if (isPlaying) {
        audioRef.current.pause();
      } else {
        audioRef.current.play();
      }
      setIsPlaying(!isPlaying);
    }
  };

  const handleTimeUpdate = () => {
    if (audioRef.current) {
      setCurrentTime(audioRef.current.currentTime);
    }
  };

  const handleLoadedMetadata = () => {
    if (audioRef.current) {
      setTotalDuration(audioRef.current.duration);
    }
  };

  const handleSeek = (e) => {
    const seekTime = parseFloat(e.target.value);
    if (audioRef.current) {
      audioRef.current.currentTime = seekTime;
      setCurrentTime(seekTime);
    }
  };

  const formatTime = (time) => {
    const minutes = Math.floor(time / 60);
    const seconds = Math.floor(time % 60);
    return `${minutes}:${seconds.toString().padStart(2, '0')}`;
  };

  return (
    <div className="flex items-center gap-2 w-full">
      <audio
        ref={audioRef}
        src={audioUrl}
        onTimeUpdate={handleTimeUpdate}
        onLoadedMetadata={handleLoadedMetadata}
        onEnded={() => setIsPlaying(false)}
        className="hidden"
      />
      <button
        onClick={handlePlayPause}
        className="w-8 h-8 rounded-full bg-fuchsia-600 text-white flex items-center justify-center hover:bg-fuchsia-700 transition-colors flex-shrink-0"
      >
        {isPlaying ? <Pause className="w-4 h-4" /> : <Play className="w-4 h-4" />}
      </button>
      <div className="flex-1 flex items-center gap-2">
        <input
          type="range"
          min="0"
          max={totalDuration || 100}
          value={currentTime}
          onChange={handleSeek}
          className="flex-1 h-1 bg-gray-200 rounded-lg appearance-none cursor-pointer"
          style={{
            background: `linear-gradient(to right, #d946ef 0%, #d946ef ${(currentTime / (totalDuration || 1)) * 100}%, #e5e7eb ${(currentTime / (totalDuration || 1)) * 100}%, #e5e7eb 100%)`
          }}
        />
        <span className="text-xs text-gray-500 w-12 text-right flex-shrink-0">
          {formatTime(currentTime)} / {formatTime(totalDuration)}
        </span>
      </div>
    </div>
  );
};

export const ChatProvider = ({ children }) => {
  const { token, user } = useAuth();
  const [isOpen, setIsOpen] = useState(false);
  const [conversations, setConversations] = useState([]);
  const [activeConversationId, setActiveConversationId] = useState(null);
  const [unreadCount, setUnreadCount] = useState(0);
  const [authError, setAuthError] = useState(false); // Track auth errors to stop retries
  const [orderConversationId, setOrderConversationId] = useState(null); // For delivery conversations

  const startConversation = useCallback(async (productId, dropshippedProductId = null, metadata = {}) => {
    if (!token) return null;

    const payload = {};
    if (productId) payload.product_id = productId;
    if (dropshippedProductId) payload.dropshipped_product_id = dropshippedProductId;

    const response = await axios.post(`${API}/conversations/start`, payload, {
      headers: { Authorization: `Bearer ${token}` },
    });

    const conversationId = response?.data?.id || response?.data?.conversationId;
    if (conversationId) {
      setIsOpen(true);
      setActiveConversationId(conversationId);
    }

    return {
      conversationId,
      productId,
      dropshippedProductId,
      metadata,
      openedAt: Date.now(),
    };
  }, [token]);

  const openConversation = useCallback((conversationId) => {
    if (!conversationId) return;
    setIsOpen(true);
    setActiveConversationId(conversationId);
  }, []);

  // Open delivery conversation for specific order
  const openOrderConversation = useCallback(async (orderId) => {
    if (!token || !orderId) return null;
    try {
      console.log('📱 [FLOATING CHAT] Opening delivery conversation for order:', orderId);
      const response = await axios.get(`${API}/chat/conversation/${orderId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      const conversation = response.data;
      if (conversation && conversation.id) {
        setOrderConversationId(conversation.id);
        setIsOpen(true);
        setActiveConversationId(conversation.id);
        console.log('✅ [FLOATING CHAT] Delivery conversation opened:', conversation.id);
        return conversation;
      }
    } catch (error) {
      console.error('❌ [FLOATING CHAT] Error opening delivery conversation:', error);
    }
    return null;
  }, [token]);

  const openChat = useCallback(() => setIsOpen(true), []);
  const closeChat = useCallback(() => setIsOpen(false), []);

  const fetchConversations = useCallback(async () => {
    if (!token || authError) return;
    try {
      const response = await axios.get(`${API}/conversations`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      const list = Array.isArray(response.data) ? response.data : (response.data?.conversations || []);
      setConversations(list);
      
      // Calculate total unread count
      const totalUnread = list.reduce((sum, conv) => {
        return sum + (conv.unread_customer || 0) + (conv.unread_seller || 0);
      }, 0);
      setUnreadCount(totalUnread);
      
      if (!activeConversationId && list.length > 0) {
        setActiveConversationId(list[0].id);
      }
    } catch (error) {
      console.error("Error loading conversations:", error);
      // Stop retrying on 401 errors - token expired
      if (error.response?.status === 401) {
        console.warn('📱 [FLOATING CHAT] Token expired, stopping conversation fetch');
        setAuthError(true);
        return;
      }
      // Don't show error toast for 401 errors - user might not be logged in
      if (error.response?.status !== 401) {
        console.error("Non-401 error loading conversations:", error);
      }
    }
  }, [token, activeConversationId, authError]);

  // Fetch delivery conversation for specific order
  const fetchOrderConversation = useCallback(async (orderId) => {
    if (!token || !orderId) return null;
    try {
      console.log('📱 [FLOATING CHAT] Fetching delivery conversation for order:', orderId);
      const response = await axios.get(`${API}/chat/conversation/${orderId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      const conversation = response.data;
      if (conversation && conversation.id) {
        setOrderConversationId(conversation.id);
        // Add to conversations list if not already there
        setConversations(prev => {
          const exists = prev.find(c => c.id === conversation.id);
          if (!exists) {
            return [conversation, ...prev];
          }
          return prev;
        });
        console.log('✅ [FLOATING CHAT] Delivery conversation fetched:', conversation.id);
        return conversation;
      }
    } catch (error) {
      console.error('❌ [FLOATING CHAT] Error fetching delivery conversation:', error);
    }
    return null;
  }, [token]);

  useEffect(() => {
    if (!isOpen) return;
    fetchConversations();
  }, [isOpen, fetchConversations]);

  // Global WebSocket connection for receiving messages even when chat is closed
  useEffect(() => {
    if (!token || !user?.id) return;
    
    // Try WebSocket connection, fall back to polling if it fails
    let wsCleanup;
    let pollingInterval;
    
    try {
      wsCleanup = createGlobalRealtime({
        token,
        onEvent: (event) => {
          if (event.type === 'new_message') {
            // Refresh conversations to update unread counts
            fetchConversations();
            
            // Show notification if message is not from current user
            if (event.message?.sender_id !== user.id) {
              const senderName = event.message?.sender_name || event.message?.sender_type === 'seller' ? 'Vendeur' : 'Client';
              
              // Show toast notification
              toast.info(`Nouveau message de ${senderName}`, {
                action: {
                  label: 'Voir',
                  onClick: () => {
                    setIsOpen(true);
                    if (event.conversation_id) {
                      setActiveConversationId(event.conversation_id);
                    } else if (event.order_id) {
                      // For delivery messages, use order_id to open conversation
                      openOrderConversation(event.order_id);
                    }
                  }
                }
              });
              
              // Show browser notification (if permission granted)
              notificationService.showNotification(`Nouveau message de ${senderName}`, {
                body: event.message?.content || event.message?.text || 'Message',
                icon: '/logo192.png',
                badge: '/badge72.png',
                tag: `chat-${event.conversation_id || event.order_id}`,
                requireInteraction: false,
                onClick: () => {
                  setIsOpen(true);
                  if (event.conversation_id) {
                    setActiveConversationId(event.conversation_id);
                  } else if (event.order_id) {
                    openOrderConversation(event.order_id);
                  }
                  window.focus();
                }
              });
            }
          }
          
          // Handle delivery chat messages
          if (event.type === 'chat_notification' && event.message) {
            fetchConversations();
            
            if (event.message?.sender_id !== user.id) {
              const senderName = event.message?.sender_name || 'Livreur';
              
              toast.info(`Nouveau message de ${senderName}`, {
                action: {
                  label: 'Voir',
                  onClick: () => {
                    setIsOpen(true);
                    if (event.order_id) {
                      openOrderConversation(event.order_id);
                    }
                  }
                }
              });
              
              notificationService.showNotification(`Nouveau message de ${senderName}`, {
                body: event.message?.content || 'Message',
                icon: '/logo192.png',
                badge: '/badge72.png',
                tag: `delivery-chat-${event.order_id}`,
                requireInteraction: false,
                onClick: () => {
                  setIsOpen(true);
                  if (event.order_id) {
                    openOrderConversation(event.order_id);
                  }
                  window.focus();
                }
              });
            }
          }
        },
        onStatusChange: (connected) => {
          console.log('Global WebSocket connection:', connected ? 'connected' : 'disconnected');
          
          // If WebSocket fails, fall back to polling
          if (!connected && !pollingInterval) {
            console.log('WebSocket failed, falling back to polling');
            pollingInterval = setInterval(() => {
              fetchConversations();
            }, 10000); // Poll every 10 seconds
          }
        }
      });
    } catch (error) {
      console.error('WebSocket initialization failed, using polling:', error);
      // Fall back to polling immediately
      pollingInterval = setInterval(() => {
        fetchConversations();
      }, 10000);
    }
    
    return () => {
      if (wsCleanup) wsCleanup();
      if (pollingInterval) clearInterval(pollingInterval);
    };
  }, [token, user?.id, fetchConversations, openOrderConversation]);

  const value = useMemo(
    () => ({
      isOpen,
      conversations,
      activeConversationId,
      unreadCount,
      orderConversationId,
      startConversation,
      openConversation,
      openOrderConversation,
      openChat,
      closeChat,
      refreshConversations: fetchConversations,
      fetchOrderConversation,
      setConversations,
      setActiveConversationId,
    }),
    [isOpen, conversations, activeConversationId, unreadCount, orderConversationId, startConversation, openConversation, openOrderConversation, openChat, closeChat, fetchConversations, fetchOrderConversation]
  );

  return <ChatContext.Provider value={value}>{children}</ChatContext.Provider>;
};

export const useChat = () => {
  const ctx = useContext(ChatContext);
  if (!ctx) {
    return {
      isOpen: false,
      conversations: [],
      activeConversationId: null,
      unreadCount: 0,
      orderConversationId: null,
      startConversation: async () => null,
      openConversation: () => {},
      openOrderConversation: async () => null,
      openChat: () => {},
      closeChat: () => {},
      refreshConversations: async () => {},
      fetchOrderConversation: async () => null,
      setConversations: () => {},
      setActiveConversationId: () => {},
    };
  }
  return ctx;
};

const FloatingChat = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { user, token, isAuthenticated } = useAuth();
  const { isOpen, closeChat, conversations, activeConversationId, openConversation, openChat, refreshConversations, setActiveConversationId, unreadCount, orderConversationId } = useChat();
  const [messages, setMessages] = useState([]);
  const [newMessage, setNewMessage] = useState("");
  const [loadingMessages, setLoadingMessages] = useState(false);
  const [showMediaMenu, setShowMediaMenu] = useState(false);
  const [uploadingFile, setUploadingFile] = useState(false);
  const [isRecording, setIsRecording] = useState(false);
  const [recordingTime, setRecordingTime] = useState(0);
  const [typingUsers, setTypingUsers] = useState([]);
  const [recordingUsers, setRecordingUsers] = useState([]);
  const [isTyping, setIsTyping] = useState(false);
  const [isRealtimeConnected, setIsRealtimeConnected] = useState(false);
  const listEndRef = useRef(null);
  const fileInputRef = useRef(null);
  const audioInputRef = useRef(null);
  const documentInputRef = useRef(null);
  const mediaRecorderRef = useRef(null);
  const recordingIntervalRef = useRef(null);
  const audioChunksRef = useRef([]);
  const typingTimeoutRef = useRef(null);
  const discardRecordingRef = useRef(false);

  const activeConversation = conversations.find((c) => c.id === activeConversationId) || null;
  const canOpenProduct =
    activeConversation?.product_id &&
    typeof activeConversation?.product_id === "string" &&
    !activeConversation?.product_id.startsWith("admin-chat-");

  const sellerShopPath = (() => {
    if (!activeConversation?.seller_id) return null;
    if (activeConversation?.seller_type === "dropshipper") {
      const slug = activeConversation?.seller_shop_slug;
      return slug ? `/boutique/${slug}` : null;
    }
    return `/vendeur-boutique/${activeConversation?.seller_id}`;
  })();

  const loadMessages = useCallback(async (forceReload = false) => {
    if (!token || !activeConversationId) return;
    setLoadingMessages(true);
    try {
      // Check if this is a delivery conversation (order_chat format)
      const isDeliveryConversation = activeConversationId.startsWith('order_chat_') || orderConversationId === activeConversationId;
      
      let response;
      if (isDeliveryConversation) {
        // For delivery conversations, use the delivery chat endpoint
        const orderId = activeConversationId.replace('order_chat_', '');
        response = await axios.get(`${API}/chat/conversation/${orderId}`, {
          headers: { Authorization: `Bearer ${token}` }
        });
      } else {
        // For product conversations, use the regular endpoint
        response = await axios.get(`${API}/conversations/${activeConversationId}`, {
          headers: { Authorization: `Bearer ${token}` }
        });
      }
      
      const newMessages = response.data?.messages || [];
      
      // On force reload, replace all messages
      if (forceReload) {
        setMessages(newMessages);
      } else {
        // Merge messages intelligently - only add new messages, don't replace all
        setMessages(prev => {
          const existingIds = new Set(prev.map(m => m.id));
          const newMessagesToAdd = newMessages.filter(m => !existingIds.has(m.id));
          
          // If we have existing messages, only add new ones
          if (prev.length > 0) {
            return [...prev, ...newMessagesToAdd];
          }
          
          // First load, use all messages
          return newMessages;
        });
      }
    } catch (error) {
      console.error("Error loading messages:", error);
      toast.error("Impossible de charger les messages");
    } finally {
      setLoadingMessages(false);
    }
  }, [token, activeConversationId, orderConversationId]);

  useEffect(() => {
    if (!isOpen || !activeConversationId) return;
    loadMessages(true); // Force reload on open
  }, [isOpen, activeConversationId]);

  // WebSocket connection for delivery conversations
  useEffect(() => {
    if (!token || !user?.id || !activeConversationId) return;
    
    // Check if this is a delivery conversation
    const isDeliveryConversation = activeConversationId.startsWith('order_chat_') || orderConversationId === activeConversationId;
    
    if (!isDeliveryConversation) return; // Only connect WebSocket for delivery conversations
    
    const orderId = activeConversationId.replace('order_chat_', '');
    const wsUrl = `${WS}/api/ws/order-chat/${orderId}`;
    const ws = new WebSocket(wsUrl);
    
    ws.onopen = () => {
      console.log('📱 [DELIVERY CHAT] WebSocket connected for order:', orderId);
    };
    
    ws.onmessage = (event) => {
      try {
        const data = JSON.parse(event.data);
        console.log('📱 [DELIVERY CHAT] WebSocket message:', data);
        
        if (data.type === 'new_message' && data.message) {
          setMessages(prev => {
            // If message already exists, don't add duplicate
            if (prev.some(message => message.id === data.message.id)) {
              return prev;
            }
            // If this is our own message, replace optimistic message
            if (data.message.sender_id === user?.id) {
              const optimisticIndex = prev.findIndex(m => m.is_optimistic && m.sender_id === user?.id);
              if (optimisticIndex >= 0) {
                const newMessages = [...prev];
                newMessages[optimisticIndex] = data.message;
                return newMessages;
              }
            }
            return [...prev, data.message];
          });
          
          // Scroll to bottom when new message arrives
          setTimeout(() => {
            listEndRef.current?.scrollIntoView({ behavior: 'smooth' });
          }, 100);
        }
        
        if (data.type === 'typing') {
          setTypingUsers(prev => {
            if (data.is_typing) {
              return [...prev.filter(u => u.id !== data.user_id), { id: data.user_id, name: data.user_name }];
            } else {
              return prev.filter(u => u.id !== data.user_id);
            }
          });
        }
      } catch (error) {
        console.error('Error parsing WebSocket message:', error);
      }
    };
    
    ws.onerror = (error) => {
      console.error('📱 [DELIVERY CHAT] WebSocket error:', error);
    };
    
    ws.onclose = () => {
      console.log('📱 [DELIVERY CHAT] WebSocket disconnected');
    };
    
    return () => {
      ws.close();
    };
  }, [token, user?.id, activeConversationId, orderConversationId, WS]);

  // HTTP polling for messages (backup if WebSocket fails)
  useEffect(() => {
    if (!isOpen || !activeConversationId || isRealtimeConnected) return;

    // Poll for new messages every 5 seconds for delivery conversations
    const pollingInterval = setInterval(() => {
      loadMessages(false); // Don't force reload, just merge new messages
      refreshConversations();
    }, 5000); // Poll every 5 seconds for faster delivery communication
    
    return () => clearInterval(pollingInterval);
  }, [isOpen, activeConversationId, isRealtimeConnected, loadMessages, refreshConversations]);

  useEffect(() => {
    listEndRef.current?.scrollIntoView({ behavior: "smooth" });
  }, [messages]);

  const handleMessageDeleted = useCallback((messageId) => {
    setMessages((prev) => prev.filter((m) => m.id !== messageId));
    refreshConversations();
  }, [refreshConversations]);

  const handleDeleteConversation = async (conversationId, e) => {
    e.stopPropagation(); // Prevent selecting the conversation when clicking delete
    
    if (!window.confirm('Êtes-vous sûr de vouloir supprimer cette conversation et tous ses messages ?')) {
      return;
    }

    try {
      await axios.delete(`${API}/conversations/${conversationId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });

      // If the deleted conversation was selected, clear selected conversation and messages
      if (activeConversationId === conversationId) {
        setActiveConversationId(null);
        setMessages([]);
      }

      // Refresh conversations to remove deleted one from list
      refreshConversations();
    } catch (error) {
      console.error('Error deleting conversation:', error);
      toast.error('Erreur lors de la suppression de la conversation');
    }
  };

  // Media upload handlers
  const handleImageUpload = async (e) => {
    const file = e.target.files[0];
    if (!file || !activeConversationId) return;

    setUploadingFile(true);
    const formData = new FormData();
    formData.append('file', file);

    try {
      const response = await axios.post(
        `${API}/conversations/${activeConversationId}/upload-image`,
        formData,
        { headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'multipart/form-data' } }
      );

      setMessages(prev => [...prev, response.data.message]);
      refreshConversations();
    } catch (error) {
      console.error('Error uploading image:', error);
      toast.error('Erreur lors de l\'envoi de l\'image');
    } finally {
      setUploadingFile(false);
      fileInputRef.current.value = '';
    }
  };

  const handleDocumentUpload = async (e) => {
    const file = e.target.files[0];
    if (!file || !activeConversationId) return;

    setUploadingFile(true);
    const formData = new FormData();
    formData.append('file', file);

    try {
      const response = await axios.post(
        `${API}/conversations/${activeConversationId}/upload-document`,
        formData,
        { headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'multipart/form-data' } }
      );

      setMessages(prev => [...prev, response.data.message]);
      refreshConversations();
    } catch (error) {
      console.error('Error uploading document:', error);
      toast.error('Erreur lors de l\'envoi du document');
    } finally {
      setUploadingFile(false);
      documentInputRef.current.value = '';
    }
  };

  const handleAudioUpload = async (e) => {
    const file = e.target.files[0];
    if (!file || !activeConversationId) return;

    setUploadingFile(true);
    const formData = new FormData();
    formData.append('file', file);

    try {
      const response = await axios.post(
        `${API}/conversations/${activeConversationId}/upload-audio`,
        formData,
        { headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'multipart/form-data' } }
      );

      setMessages(prev => [...prev, response.data.message]);
      refreshConversations();
    } catch (error) {
      console.error('Error uploading audio:', error);
      toast.error('Erreur lors de l\'envoi de l\'audio');
    } finally {
      setUploadingFile(false);
      audioInputRef.current.value = '';
    }
  };

  // Audio recording functionality
  const startRecording = async () => {
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      mediaRecorderRef.current = new MediaRecorder(stream);
      audioChunksRef.current = [];
      
      mediaRecorderRef.current.ondataavailable = (e) => {
        audioChunksRef.current.push(e.data);
      };
      
      mediaRecorderRef.current.onstop = async () => {
        if (discardRecordingRef.current) {
          discardRecordingRef.current = false;
          stream.getTracks().forEach(track => track.stop());
          audioChunksRef.current = [];
          return;
        }
        const audioBlob = new Blob(audioChunksRef.current, { type: 'audio/webm' });
        const audioFile = new File([audioBlob], 'recording.webm', { type: 'audio/webm' });
        
        setUploadingFile(true);
        const formData = new FormData();
        formData.append('file', audioFile);
        formData.append('duration', recordingTime);
        
        try {
          const response = await axios.post(
            `${API}/conversations/${activeConversationId}/upload-audio`,
            formData,
            { headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'multipart/form-data' } }
          );
          
          setMessages(prev => [...prev, response.data.message]);
          refreshConversations();
        } catch (error) {
          console.error('Error uploading recording:', error);
          toast.error('Erreur lors de l\'envoi du message vocal');
        } finally {
          setUploadingFile(false);
        }
        
        // Stop all tracks
        stream.getTracks().forEach(track => track.stop());
        audioChunksRef.current = [];
      };
      
      mediaRecorderRef.current.start();
      discardRecordingRef.current = false;
      setIsRecording(true);
      setRecordingTime(0);
      
      recordingIntervalRef.current = setInterval(() => {
        setRecordingTime(prev => prev + 1);
      }, 1000);
      
    } catch (error) {
      console.error('Error starting recording:', error);
      toast.error('Impossible d\'accéder au microphone');
      throw error;
    }
  };

  const stopRecording = () => {
    if (mediaRecorderRef.current && isRecording) {
      mediaRecorderRef.current.stop();
      setIsRecording(false);
      if (recordingIntervalRef.current) {
        clearInterval(recordingIntervalRef.current);
      }
      setRecordingTime(0);
    }
  };

  const cancelRecording = () => {
    if (mediaRecorderRef.current && isRecording) {
      discardRecordingRef.current = true;
      mediaRecorderRef.current.stop();
      setIsRecording(false);
      if (recordingIntervalRef.current) {
        clearInterval(recordingIntervalRef.current);
      }
      setRecordingTime(0);
      audioChunksRef.current = [];
      toast.info('Enregistrement annulé');
    }
  };

  const formatRecordingTime = (seconds) => {
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${mins}:${secs.toString().padStart(2, '0')}`;
  };

  const handleSend = async (e) => {
    e.preventDefault();
    if (!newMessage.trim() || !activeConversationId || !token) return;
    const content = newMessage.trim();
    setNewMessage("");
    
    // Stop typing indicator
    await setTypingStatus(false);
    
    // Optimistic update
    const optimisticMessage = {
      id: `temp-${Date.now()}`,
      content: content,
      sender_id: user?.id,
      sender_type: 'customer',
      created_at: new Date().toISOString(),
      is_read: false,
      is_optimistic: true
    };
    setMessages(prev => [...prev, optimisticMessage]);
    
    try {
      // Check if this is a delivery conversation
      const isDeliveryConversation = activeConversationId.startsWith('order_chat_') || orderConversationId === activeConversationId;
      
      if (isDeliveryConversation) {
        // For delivery conversations, use the delivery chat endpoint
        const orderId = activeConversationId.replace('order_chat_', '');
        await axios.post(
          `${API}/chat/send`,
          { 
            order_id: orderId,
            content: content,
            message_type: 'text'
          },
          { headers: { Authorization: `Bearer ${token}` } }
        );
      } else {
        // For product conversations, use the regular endpoint
        await axios.post(
          `${API}/conversations/${activeConversationId}/messages`,
          { content },
          { headers: { Authorization: `Bearer ${token}` } }
        );
      }
      
      // Message will be updated via WebSocket
      refreshConversations();
    } catch (error) {
      setMessages(prev => prev.filter(m => m.id !== optimisticMessage.id));
      setNewMessage(content);
      toast.error("Erreur lors de l'envoi");
    }
  };
  
  // Typing indicator functionality
  const setTypingStatus = async (isTyping) => {
    if (!activeConversationId || !token) return;
    try {
      await axios.post(
        `${API}/conversations/${activeConversationId}/typing`,
        { is_typing: isTyping },
        { headers: { Authorization: `Bearer ${token}` } }
      );
    } catch (error) {
      console.error('Error setting typing status:', error);
    }
  };
  
  const handleTyping = (e) => {
    const value = e.target.value;
    setNewMessage(value);
    
    if (value.trim() && !isTyping) {
      setIsTyping(true);
      setTypingStatus(true);
      
      // Clear previous timeout
      if (typingTimeoutRef.current) {
        clearTimeout(typingTimeoutRef.current);
      }
      
      // Stop typing after 3 seconds of inactivity
      typingTimeoutRef.current = setTimeout(() => {
        setIsTyping(false);
        setTypingStatus(false);
      }, 3000);
    } else if (!value.trim() && isTyping) {
      setIsTyping(false);
      setTypingStatus(false);
    }
  };
  
  // Voice recording indicator functionality
  const setVoiceRecordingStatus = async (isRecording) => {
    if (!activeConversationId || !token) return;
    try {
      await axios.post(
        `${API}/conversations/${activeConversationId}/voice-recording`,
        { is_recording: isRecording },
        { headers: { Authorization: `Bearer ${token}` } }
      );
    } catch (error) {
      console.error('Error setting voice recording status:', error);
    }
  };
  
  // Override recording functions to send status
  const startRecordingWithStatus = async () => {
    try {
      await startRecording();
      await setVoiceRecordingStatus(true);
    } catch (_) {
      await setVoiceRecordingStatus(false);
    }
  };
  
  const stopRecordingWithStatus = () => {
    setVoiceRecordingStatus(false);
    stopRecording();
  };
  
  const cancelRecordingWithStatus = () => {
    setVoiceRecordingStatus(false);
    cancelRecording();
  };
  
  // Authenticated WebSocket provides immediate message, typing and recording events.
  useEffect(() => {
    if (!isOpen || !activeConversationId || !token) return undefined;
    return createChatRealtime({
      conversationId: activeConversationId,
      token,
      onStatusChange: setIsRealtimeConnected,
      onEvent: (event) => {
        // Handle new messages from any conversation
        if (event.type === 'new_message' && event.message) {
          // If message is for current conversation, add it
          if (event.conversation_id === activeConversationId || !event.conversation_id) {
            setMessages(prev => {
              // If message already exists, don't add duplicate
              if (prev.some(message => message.id === event.message.id)) {
                return prev;
              }
              // If this is our own message, replace optimistic message
              if (event.message.sender_id === user?.id) {
                const optimisticIndex = prev.findIndex(m => m.is_optimistic && m.sender_id === user?.id);
                if (optimisticIndex >= 0) {
                  const newMessages = [...prev];
                  newMessages[optimisticIndex] = event.message;
                  return newMessages;
                }
              }
              return [...prev, event.message];
            });
            if (event.message.sender_id !== user?.id) {
              setTypingUsers([]);
              setRecordingUsers([]);
            }
          }
          // Always refresh conversations to update unread counts
          refreshConversations();
          return;
        }
        if (event.type === 'message_deleted' && event.message_id) {
          setMessages(prev => prev.filter(message => message.id !== event.message_id));
          return;
        }
        // Don't show own typing/recording indicators
        if (event.user_id === user?.id) return;
        
        // Handle typing status - show if it's for the current conversation
        if (event.type === 'typing_status') {
          if (event.conversation_id === activeConversationId || !event.conversation_id) {
            const participant = { id: event.user_id, name: activeConversation?.other_party_name || activeConversation?.seller_name || 'Votre interlocuteur' };
            setTypingUsers(event.is_typing ? [participant] : []);
          }
          return;
        }
        
        // Handle voice recording status - show if it's for the current conversation
        if (event.type === 'voice_recording_status') {
          if (event.conversation_id === activeConversationId || !event.conversation_id) {
            const participant = { id: event.user_id, name: activeConversation?.other_party_name || activeConversation?.seller_name || 'Votre interlocuteur' };
            setRecordingUsers(event.is_recording ? [participant] : []);
          }
          return;
        }
      },
    });
  }, [isOpen, activeConversationId, token, user?.id, activeConversation?.other_party_name, activeConversation?.seller_name, refreshConversations]);

  // Fallback for networks that block WebSocket connections.
  // Disabled for now due to Apache WebSocket configuration issues
  // useEffect(() => {
  //   if (!isOpen || !activeConversationId || !token || isRealtimeConnected) return;
  //   
  //   const pollInterval = setInterval(async () => {
  //     try {
  //       const [typingResponse, recordingResponse] = await Promise.all([
  //         axios.get(`${API}/conversations/${activeConversationId}/typing-users`, {
  //           headers: { Authorization: `Bearer ${token}` }
  //         }),
  //         axios.get(`${API}/conversations/${activeConversationId}/voice-recording-users`, {
  //           headers: { Authorization: `Bearer ${token}` }
  //         })
  //       ]);
  //       
  //       setTypingUsers(typingResponse.data?.typing_users || []);
  //       setRecordingUsers(recordingResponse.data?.recording_users || []);
  //     } catch (error) {
  //       console.error('Error polling status:', error);
  //     }
  //   }, 3000);
  //   
  //   return () => clearInterval(pollInterval);
  // }, [isOpen, activeConversationId, token, isRealtimeConnected]);

  // Hide floating chat button on chat pages
  const isChatPage = location.pathname === '/messages' || location.pathname.startsWith('/message');

  if (isChatPage) {
    return null; // Don't render floating chat button on chat pages
  }

  if (!isOpen) {
    if (!isAuthenticated) {
      return (
        <button
          onClick={() => toast.error("Connectez-vous pour ouvrir le chat")}
          className="fixed bottom-[5.9rem] md:bottom-4 right-3 md:right-4 z-[90] group"
          aria-label="Ouvrir la messagerie"
        >
          <span className="absolute -inset-1 rounded-full bg-gradient-to-r from-fuchsia-600 via-orange-500 to-amber-500 blur opacity-70 group-hover:opacity-100 animate-pulse" />
          <span className="relative w-16 h-16 rounded-full bg-gradient-to-r from-fuchsia-600 via-orange-500 to-amber-500 shadow-2xl flex items-center justify-center overflow-visible">
            <span className="absolute inset-0 -translate-x-full bg-gradient-to-r from-transparent via-white/35 to-transparent group-hover:translate-x-full transition-transform duration-1000" />
            <MessageCircle className="w-7 h-7 text-white relative z-10" />
            <span className="absolute top-0 right-0 translate-x-1 -translate-y-1 px-1.5 py-0.5 rounded-full text-[10px] leading-none font-bold bg-slate-700 text-white border border-white shadow">
              Chat
            </span>
          </span>
        </button>
      );
    }
    return (
      <button
        onClick={openChat}
        className="fixed bottom-[5.9rem] md:bottom-4 right-3 md:right-4 z-[90] group"
        aria-label="Ouvrir la messagerie"
      >
        <span className="absolute -inset-1 rounded-full bg-gradient-to-r from-fuchsia-600 via-orange-500 to-amber-500 blur opacity-70 group-hover:opacity-100 animate-pulse" />
        <span className="relative w-16 h-16 rounded-full bg-gradient-to-r from-fuchsia-600 via-orange-500 to-amber-500 shadow-2xl flex items-center justify-center overflow-visible">
          <span className="absolute inset-0 -translate-x-full bg-gradient-to-r from-transparent via-white/35 to-transparent group-hover:translate-x-full transition-transform duration-1000" />
          <MessageCircle className="w-7 h-7 text-white relative z-10" />
          {unreadCount > 0 && (
            <span className="absolute top-0 right-0 translate-x-1 -translate-y-1 px-1.5 py-0.5 rounded-full text-[10px] leading-none font-bold bg-red-500 text-white border border-white shadow">
              {unreadCount > 9 ? '9+' : unreadCount}
            </span>
          )}
          {unreadCount === 0 && (
            <span className="absolute top-0 right-0 translate-x-1 -translate-y-1 px-1.5 py-0.5 rounded-full text-[10px] leading-none font-bold bg-emerald-500 text-white border border-white shadow">
              En ligne
            </span>
          )}
        </span>
      </button>
    );
  }

  return (
    <div className="fixed bottom-[5.9rem] md:bottom-4 right-2 md:right-4 z-[90] w-[96vw] md:w-[95vw] max-w-[420px] h-[68vh] md:h-[75vh] max-h-[620px] bg-white border border-slate-200 rounded-2xl shadow-2xl overflow-hidden">
      <div className="h-14 bg-gradient-to-r from-fuchsia-600 via-orange-500 to-amber-500 text-white flex items-center justify-between px-4">
        <div className="flex items-center gap-2 font-semibold">
          <MessageCircle className="w-4 h-4" />
          Messagerie
        </div>
        <Button variant="ghost" size="icon" className="text-white hover:bg-white/20" onClick={closeChat}>
          <X className="w-4 h-4" />
        </Button>
      </div>

      <div className="h-[calc(100%-56px)] flex">
        <div className="w-48 sm:w-56 border-r border-slate-200 overflow-y-auto bg-slate-50">
          {conversations.length === 0 ? (
            <div className="p-3 text-xs text-slate-500">Aucune conversation</div>
          ) : (
            conversations.map((conv) => (
              <div
                key={conv.id}
                className={`relative w-full text-left p-4 border-b border-slate-200 hover:bg-slate-100 ${conv.id === activeConversationId ? "bg-white" : ""}`}
              >
                <button
                  onClick={() => {
                    openConversation(conv.id);
                    loadMessages(true); // Force reload on conversation change
                  }}
                  className="w-full text-left pr-8"
                >
                  <div className="flex items-center gap-4">
                    {conv?.seller_avatar ? (
                      <MediaImg 
                        src={conv.seller_avatar} 
                        alt={conv.seller_name}
                        className="w-14 h-14 rounded-full object-cover border-2 border-slate-200"
                      />
                    ) : (
                      <div className="w-14 h-14 rounded-full bg-gradient-to-br from-fuchsia-500 to-orange-500 flex items-center justify-center text-white font-bold text-lg">
                        {conv?.seller_name?.[0] || "C"}
                      </div>
                    )}
                    <div className="flex-1 min-w-0">
                      <p className="text-sm font-semibold truncate">{conv?.seller_name || "Contact"}</p>
                      <p className="text-xs text-slate-500 truncate">{conv?.product_name || "Discussion"}</p>
                    </div>
                  </div>
                </button>
                <button
                  onClick={(e) => handleDeleteConversation(conv.id, e)}
                  className="absolute top-4 right-2 p-1 hover:bg-red-100 rounded transition-colors"
                  title="Supprimer la conversation"
                >
                  <X className="w-4 h-4 text-red-500 hover:text-red-700" />
                </button>
              </div>
            ))
          )}
        </div>

        <div className="flex-1 flex flex-col">
          <div className="h-10 px-3 border-b border-slate-200 flex items-center justify-between gap-2 min-w-0">
            {activeConversation?.seller_name && sellerShopPath ? (
              <div className="flex items-center gap-2 flex-1 min-w-0">
                {activeConversation?.seller_avatar ? (
                  <MediaImg 
                    src={activeConversation?.seller_avatar} 
                    alt={activeConversation?.seller_name}
                    className="w-8 h-8 rounded-full object-cover border-2 border-slate-200"
                  />
                ) : (
                  <div className="w-8 h-8 rounded-full bg-gradient-to-br from-fuchsia-500 to-orange-500 flex items-center justify-center text-white font-bold text-xs">
                    {activeConversation?.seller_name?.[0] || "S"}
                  </div>
                )}
                <button
                  type="button"
                  onClick={() => navigate(sellerShopPath)}
                  className="text-xs font-semibold text-fuchsia-600 hover:text-fuchsia-700 truncate"
                  title="Voir la boutique"
                >
                  {activeConversation.seller_name}
                </button>
              </div>
            ) : (
              <div className="flex items-center gap-2 flex-1 min-w-0">
                {activeConversation?.seller_avatar ? (
                  <MediaImg 
                    src={activeConversation?.seller_avatar} 
                    alt={activeConversation?.seller_name}
                    className="w-8 h-8 rounded-full object-cover border-2 border-slate-200"
                  />
                ) : (
                  <div className="w-8 h-8 rounded-full bg-gradient-to-br from-fuchsia-500 to-orange-500 flex items-center justify-center text-white font-bold text-xs">
                    {activeConversation?.seller_name?.[0] || "S"}
                  </div>
                )}
                <span className="text-xs font-semibold text-slate-900 truncate">
                  {activeConversation?.seller_name || "Contact"}
                </span>
              </div>
            )}
          </div>

          {canOpenProduct && (
            <button
              type="button"
              onClick={() => navigate(`/produit/${activeConversation?.product_id}`)}
              className="px-3 py-2 border-b border-slate-200 hover:bg-slate-50 flex items-center gap-2 text-left"
            >
              {activeConversation?.product_image ? (
                <MediaImg
                  src={activeConversation?.product_image}
                  alt={activeConversation?.product_name || "Produit"}
                  className="w-10 h-10 rounded object-cover border border-slate-200"
                />
              ) : (
                <div className="w-10 h-10 rounded bg-slate-100 border border-slate-200" />
              )}
              <div className="min-w-0">
                <p className="text-xs font-semibold text-slate-800 truncate">{activeConversation?.product_name || "Produit"}</p>
                <div className="flex items-center gap-2 mt-0.5">
                  {activeConversation?.product_promo_price_fcfa ? (
                    <>
                      <span className="text-[11px] font-bold text-emerald-600">
                        {new Intl.NumberFormat("fr-FR").format(activeConversation?.product_promo_price_fcfa)} FCFA
                      </span>
                      {activeConversation?.product_price_fcfa && (
                        <span className="text-[10px] text-slate-400 line-through">
                          {new Intl.NumberFormat("fr-FR").format(activeConversation?.product_price_fcfa)} FCFA
                        </span>
                      )}
                    </>
                  ) : activeConversation?.product_price_fcfa ? (
                    <span className="text-[11px] font-bold text-slate-700">
                      {new Intl.NumberFormat("fr-FR").format(activeConversation?.product_price_fcfa)} FCFA
                    </span>
                  ) : null}
                </div>
                <p className="text-[11px] text-fuchsia-600 mt-0.5">Voir les détails du produit</p>
              </div>
            </button>
          )}

          <div className="flex-1 overflow-y-auto p-3 space-y-2 bg-white">
            {loadingMessages ? (
              <p className="text-xs text-slate-500">Chargement...</p>
            ) : messages.length === 0 ? (
              <p className="text-xs text-slate-500">Aucun message</p>
            ) : (
              <>
                {/* Typing indicator */}
                {typingUsers.length > 0 && (
                  <div className="flex items-center gap-2 px-3 py-2 bg-slate-100 rounded-lg">
                    <div className="flex gap-1">
                      <div className="w-2 h-2 bg-slate-400 rounded-full animate-bounce" style={{ animationDelay: '0ms' }} />
                      <div className="w-2 h-2 bg-slate-400 rounded-full animate-bounce" style={{ animationDelay: '150ms' }} />
                      <div className="w-2 h-2 bg-slate-400 rounded-full animate-bounce" style={{ animationDelay: '300ms' }} />
                    </div>
                    <span className="text-xs text-slate-600">
                      {typingUsers.length === 1 
                        ? `${typingUsers[0]?.name || 'Quelqu\'un'} est en train d'écrire...`
                        : `${typingUsers.length} personnes sont en train d'écrire...`
                      }
                    </span>
                  </div>
                )}
                
                {/* Voice recording indicator */}
                {recordingUsers.length > 0 && (
                  <div className="flex items-center gap-2 px-3 py-2 bg-red-50 rounded-lg border border-red-200">
                    <div className="w-2 h-2 bg-red-500 rounded-full animate-pulse" />
                    <span className="text-xs text-red-600">
                      {recordingUsers.length === 1 
                        ? `${recordingUsers[0]?.name || 'Quelqu\'un'} est en train d'enregistrer un message vocal...`
                        : `${recordingUsers.length} personnes sont en train d'enregistrer...`
                      }
                    </span>
                  </div>
                )}
                
                {messages.map((m) => {
                  const isOwn = m.sender_id === user?.id;
                  const isCustomerBubble = m.sender_type === "customer";
                  return (
                    <div
                      key={m.id}
                      className={`flex items-end gap-1 max-w-[85%] ${
                        isCustomerBubble ? "ml-0" : "ml-auto flex-row-reverse"
                      }`}
                    >
                      <div
                        className={`px-3 py-2 rounded-xl text-xs ${
                          isCustomerBubble ? "bg-slate-100 text-slate-800" : "bg-fuchsia-600 text-white"
                        }`}
                      >
                        {m.media_type === 'image' && (
                          <div>
                            <MediaImg 
                              src={m.file_url} 
                              alt="Image partagée" 
                              className="max-w-[200px] max-h-[200px] rounded-lg object-contain"
                            />
                          </div>
                        )}
                        {m.media_type === 'document' && (
                          <div className="flex items-center gap-2 p-2 bg-blue-50 rounded-lg border border-blue-200">
                            <div className="w-8 h-8 bg-blue-100 rounded-lg flex items-center justify-center flex-shrink-0">
                              <FileText className="w-4 h-4 text-blue-600" />
                            </div>
                            <div className="flex-1 min-w-0">
                              <p className="text-xs font-medium text-blue-900 truncate">{m.file_name}</p>
                              <p className="text-[10px] text-blue-600">
                                {m.file_size ? `${(m.file_size / 1024).toFixed(1)} KB` : 'Document'}
                              </p>
                            </div>
                            <a 
                              href={m.file_url} 
                              target="_blank" 
                              rel="noopener noreferrer"
                              className="flex items-center gap-1 px-2 py-1 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors text-xs font-medium"
                            >
                              <FileText className="w-3 h-3" />
                              Télécharger
                            </a>
                          </div>
                        )}
                        {m.media_type === 'audio' && (
                          <div className="space-y-1">
                            <CustomAudioPlayer audioUrl={m.file_url} duration={m.duration} />
                          </div>
                        )}
                        {m.content || m.text}
                      </div>
                      {isOwn && activeConversationId && (
                        <ChatMessageDeleteButton
                          token={token}
                          conversationId={activeConversationId}
                          messageId={m.id}
                          onDeleted={handleMessageDeleted}
                          className={
                            isCustomerBubble
                              ? "text-slate-400 hover:text-red-500"
                              : "text-fuchsia-200 hover:text-white"
                          }
                          disabled={String(m.id).startsWith("temp-")}
                        />
                      )}
                    </div>
                  );
                })}
              </>
            )}
            <div ref={listEndRef} />
          </div>

          <form onSubmit={handleSend} className="p-2 border-t border-slate-200">
            <div className="flex items-center gap-2">
              <div className="relative">
                <Button
                  type="button"
                  variant="ghost"
                  size="icon"
                  onClick={() => setShowMediaMenu(!showMediaMenu)}
                  className="h-9 w-9 text-slate-500 hover:text-slate-700"
                >
                  <Upload className="w-4 h-4" />
                </Button>
                
                {showMediaMenu && (
                  <div className="absolute bottom-10 left-0 bg-white rounded-lg shadow-lg border border-slate-200 py-1 z-10 min-w-[140px]">
                    <Button
                      type="button"
                      variant="ghost"
                      size="sm"
                      onClick={() => {
                        fileInputRef.current?.click();
                        setShowMediaMenu(false);
                      }}
                      className="w-full justify-start"
                    >
                      <ImageIcon className="w-4 h-4 mr-2" />
                      Image
                    </Button>
                    <Button
                      type="button"
                      variant="ghost"
                      size="sm"
                      onClick={() => {
                        documentInputRef.current?.click();
                        setShowMediaMenu(false);
                      }}
                      className="w-full justify-start"
                    >
                      <FileText className="w-4 h-4 mr-2" />
                      Document
                    </Button>
                    <Button
                      type="button"
                      variant="ghost"
                      size="sm"
                      onClick={() => {
                        audioInputRef.current?.click();
                        setShowMediaMenu(false);
                      }}
                      className="w-full justify-start"
                    >
                      <Radio className="w-4 h-4 mr-2" />
                      Audio
                    </Button>
                    <div className="border-t pt-1">
                      {isRecording ? (
                        <div className="space-y-1 px-2">
                          <div className="text-center text-xs font-medium text-fuchsia-600">
                            {formatRecordingTime(recordingTime)}
                          </div>
                          <div className="flex gap-1">
                            <Button
                              type="button"
                              variant="ghost"
                              size="sm"
                              onClick={cancelRecordingWithStatus}
                              className="flex-1 justify-start text-xs text-red-600"
                            >
                              Annuler
                            </Button>
                            <Button
                              type="button"
                              variant="ghost"
                              size="sm"
                              onClick={stopRecordingWithStatus}
                              className="flex-1 justify-start text-xs text-green-600"
                            >
                              Envoyer
                            </Button>
                          </div>
                        </div>
                      ) : (
                        <Button
                          type="button"
                          variant="ghost"
                          size="sm"
                          onClick={startRecordingWithStatus}
                          className="w-full justify-start"
                        >
                          <Mic className="w-4 h-4 mr-2" />
                          Enregistrer
                        </Button>
                      )}
                    </div>
                  </div>
                )}
              </div>
              
                      <Input
                        value={newMessage}
                        onChange={handleTyping}
                        placeholder="Écrire un message..."
                        className="h-9 text-sm flex-1"
                      />
              <Button type="submit" size="icon" className="h-9 w-9 bg-fuchsia-600 hover:bg-fuchsia-700" disabled={uploadingFile}>
                {uploadingFile ? (
                  <div className="w-4 h-4 border-2 border-fuchsia-200 border-t-fuchsia-600 rounded-full animate-spin" />
                ) : (
                  <Send className="w-4 h-4" />
                )}
              </Button>
            </div>
            
            {/* Hidden file inputs */}
            <input
              ref={fileInputRef}
              type="file"
              accept="image/*"
              onChange={handleImageUpload}
              className="hidden"
            />
            <input
              ref={documentInputRef}
              type="file"
              accept=".pdf,.doc,.docx,.txt"
              onChange={handleDocumentUpload}
              className="hidden"
            />
            <input
              ref={audioInputRef}
              type="file"
              accept="audio/*"
              onChange={handleAudioUpload}
              className="hidden"
            />
          </form>
        </div>
      </div>
    </div>
  );
};

export default FloatingChat;
